import { useApolloClient, useMutation } from "@apollo/client";
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import {
  WasteClassification,
  WasteStreamRegistrationMatchesQuery,
} from "generated-graphql/graphql";
import { useMemo, useState } from "react";
import { CREATE_WASTE_STREAM_REGISTRATION_SUBMISSIONS_MUTATION } from "routes/Customer/Waste/WasteStreams/api";

import { WasteStreamMergeDialog } from "../Facilities/Facility/WasteStreamsDataGrid/WasteStreamMerge/WasteStreamMergeDialog";
import {
  LINK_WASTE_STREAM_TO_REGISTRATION_MUTATION,
  useWasteStreamRegistrationMatches,
} from "./api";
import { MatchTab } from "./MatchTab";
import { AUTO_SENTINEL, MethodOverride, RegisterNewTab } from "./RegisterNewTab";
import { WasteStreamSummaryFields } from "./WasteStreamSummaryFields";

type Tab = "match" | "new";

type Match = WasteStreamRegistrationMatchesQuery["wasteStreamRegistrationMatches"][number];
type MergeTarget = NonNullable<Match["relatedStream"]>;

export interface StateRegistrationDialogStream {
  id: string;
  name: string;
  // The grid-info fields shown in the current-stream summary box. Optional and
  // nullable so both grids' generated row types satisfy the shape; the dialog
  // is always handed a full grid row.
  eManifestProfileNumber?: string | null;
  vendorProfileNumber?: string | null;
  federalWasteCodes?: string[] | null;
  stateWasteCodes?: string[] | null;
  classification?: WasteClassification | null;
  formCode?: string | null;
  sourceCode?: string | null;
}

export interface StateRegistrationDialogProps<Row extends StateRegistrationDialogStream> {
  open: boolean;
  onClose: () => void;
  facilityId: string;
  facilityName: string;
  state: string;
  /**
   * The stream this dialog was opened for. Forwarded to the merge wizard as
   * its second (losing) row — full grid row objects flow through untouched,
   * since `WasteStreamMergeDialog.selectedRows` is loosely typed.
   */
  wasteStream: Row;
}

/**
 * Refetches the two grid queries after a link / register-new action,
 * scoped to this facility — mirrors `WasteStreamMergeDialog`'s
 * `client.refetchQueries` pattern so a stale instance of `WasteStreams` for
 * a *different*, previously-visited facility isn't refetched. `TenantWasteStreams`
 * has no `facilityId` variable, so it always refetches.
 */
function refetchWasteStreamGridQueries(
  client: ReturnType<typeof useApolloClient>,
  facilityId: string
) {
  return client.refetchQueries({
    include: ["WasteStreams", "TenantWasteStreams"],
    onQueryUpdated(observableQuery) {
      const queryFacilityId = observableQuery.variables?.facilityId;
      if (queryFacilityId && queryFacilityId !== facilityId) {
        return false;
      }
      return observableQuery.refetch();
    },
  });
}

/**
 * Unified staff-only "+ State Registration" dialog — replaces the grid's
 * former per-stream "+ Submission" button. Two tabs: Match existing (default,
 * shown first) and Register new. A summary box above the tabs shows the
 * stream's grid info for context.
 *
 * The Match tab lists ranked registration candidates. A candidate that already
 * owns a waste stream is marked MERGE: confirming it folds this stream into
 * that one through the existing merge wizard, so a registration never ends up
 * with two unmerged streams. A bare candidate is linked directly.
 */
export function StateRegistrationDialog<Row extends StateRegistrationDialogStream>({
  open,
  onClose,
  facilityId,
  facilityName,
  state,
  wasteStream,
}: StateRegistrationDialogProps<Row>) {
  const alerts = useAlerts();
  const client = useApolloClient();

  const [tab, setTab] = useState<Tab>("match");

  const [selectedRegistrationId, setSelectedRegistrationId] = useState<string | null>(null);
  const [methodOverride, setMethodOverride] = useState<MethodOverride>(AUTO_SENTINEL);
  const [mergeRows, setMergeRows] = useState<Array<Row | MergeTarget> | null>(null);

  const { data: matchData, loading: matchesLoading } = useWasteStreamRegistrationMatches(
    wasteStream.id,
    !open
  );
  const matches = useMemo(() => matchData?.wasteStreamRegistrationMatches ?? [], [matchData]);

  // A candidate whose registration already owns another stream is a merge, not a
  // link. The server resolves that stream onto the candidate, so the two arrive
  // together and a confirm can never race a separate merge-targets query.
  const mergeTargetByRegistrationId = useMemo(() => {
    const map = new Map<string, MergeTarget>();
    for (const match of matches) {
      if (match.relatedStream) {
        map.set(match.registration.id, match.relatedStream);
      }
    }
    return map;
  }, [matches]);

  const relatedStreamNameByRegistrationId = useMemo(() => {
    const map = new Map<string, string>();
    for (const [registrationId, target] of mergeTargetByRegistrationId) {
      map.set(registrationId, target.name);
    }
    return map;
  }, [mergeTargetByRegistrationId]);

  const selectedMergeTarget = selectedRegistrationId
    ? mergeTargetByRegistrationId.get(selectedRegistrationId)
    : undefined;

  const [linkRegistration, { loading: linking }] = useMutation(
    LINK_WASTE_STREAM_TO_REGISTRATION_MUTATION
  );
  const [createSubmissions, { loading: creating }] = useMutation(
    CREATE_WASTE_STREAM_REGISTRATION_SUBMISSIONS_MUTATION
  );

  const handleClose = () => {
    setSelectedRegistrationId(null);
    setMergeRows(null);
    onClose();
  };

  const handleConfirmMatch = async () => {
    if (!selectedRegistrationId) return;
    // The selected registration already owns a stream — fold this stream into
    // it via the existing merge wizard rather than adding a second link.
    if (selectedMergeTarget) {
      // Target/winner first, this stream second — matches
      // WasteStreamMergeDialog's convention of keeping the first row's FK.
      setMergeRows([selectedMergeTarget, wasteStream]);
      return;
    }
    try {
      await linkRegistration({
        variables: { wasteStreamId: wasteStream.id, registrationId: selectedRegistrationId },
      });
      await refetchWasteStreamGridQueries(client, facilityId);
      alerts.success("Waste stream linked to the registration.");
      handleClose();
    } catch (e) {
      alerts.error("Failed to link the waste stream to that registration.", e);
    }
  };

  const handleRegisterNew = async () => {
    try {
      const result = await createSubmissions({
        variables: {
          input: {
            wasteStreamIds: [wasteStream.id],
            submissionMethod: methodOverride === AUTO_SENTINEL ? undefined : methodOverride,
          },
        },
      });
      const summary = result.data?.createWasteStreamRegistrationSubmissions;
      const failed = summary?.failed ?? 0;
      const created = summary?.created ?? 0;
      const skipped = summary?.skipped ?? 0;
      if (failed > 0) {
        alerts.error("Failed to create the registration.");
        return;
      }
      await refetchWasteStreamGridQueries(client, facilityId);
      // The server skips (rather than fails) a stream that already has an open
      // registration submission — surface that instead of a false "created".
      if (created === 0 && skipped > 0) {
        alerts.info("This stream already has an open registration submission.");
      } else {
        alerts.success("Registration created.");
      }
      handleClose();
    } catch (e) {
      alerts.error("Failed to create the registration.", e);
    }
  };

  // Deep-links to the existing two-step merge wizard, which owns its own
  // dialog chrome — swap this dialog out for it entirely rather than
  // stacking dialogs.
  if (mergeRows) {
    return (
      <WasteStreamMergeDialog
        selectedRows={mergeRows}
        onClose={() => {
          setMergeRows(null);
          handleClose();
        }}
      />
    );
  }

  const primaryActionButtonProps =
    tab === "match"
      ? {
          onClick: handleConfirmMatch,
          disabled: !selectedRegistrationId || linking,
          loading: linking,
          saveText: selectedMergeTarget ? "Merge streams" : "Confirm match",
          hideStartIcon: true,
        }
      : {
          onClick: handleRegisterNew,
          disabled: creating,
          loading: creating,
          saveText: "Create registration",
          hideStartIcon: true,
        };

  const helpText =
    tab === "match"
      ? "Select an existing state registration for this waste stream. If the state registration selected has another waste stream, this waste stream will be merged into it."
      : "Create a Submission to register this waste stream with the state. This waste stream's state registration will show as In Progress.";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      // Fixed height so switching Match <-> Register new doesn't resize and
      // re-center the dialog — the (taller) match list scrolls within it.
      PaperProps={{ sx: { height: "80vh" } }}
      title="State Registration"
      primaryActionButtonProps={primaryActionButtonProps}
      stickyHeaderContent={
        <Stack spacing={1.5} sx={{ pb: 1 }}>
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              p: 1.5,
              backgroundColor: "background.default",
            }}
          >
            <Typography variant="subtitle2">{wasteStream.name}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {facilityName} · {state}
            </Typography>
            <WasteStreamSummaryFields data={wasteStream} includeSourceCode />
          </Box>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={tab}
            onChange={(_event, value: Tab | null) => {
              if (value) setTab(value);
            }}
          >
            <ToggleButton value="match">Match existing</ToggleButton>
            <ToggleButton value="new">Register new</ToggleButton>
          </ToggleButtonGroup>

          <Typography variant="body2" color="text.secondary">
            {helpText}
          </Typography>
        </Stack>
      }
    >
      {tab === "match" && (
        <MatchTab
          matches={matches}
          loading={matchesLoading}
          selectedRegistrationId={selectedRegistrationId}
          onSelect={setSelectedRegistrationId}
          relatedStreamNameByRegistrationId={relatedStreamNameByRegistrationId}
        />
      )}
      {tab === "new" && (
        <RegisterNewTab
          facilityName={facilityName}
          state={state}
          methodOverride={methodOverride}
          onMethodChange={setMethodOverride}
        />
      )}
    </Dialog>
  );
}
